import DomainSummary from "@/components/DomainSummary";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Route as RouteIcon, Plus, Trash2, ArrowRight, Play, ListOrdered } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PageHeader, Loading, EmptyState } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

export default function Routing() {
  const [routes, setRoutes] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ path_prefix: "/", kind: "redirect", target: "" });
  const [test, setTest] = useState({ host: "play.studyformath.site", path: "/" });
  const [result, setResult] = useState(null);
  const { canWrite } = useAuth();

  const load = () => api.get("/routes").then(setRoutes).catch(() => setRoutes([]));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.path_prefix || !form.target) { toast.error("Path and target required"); return; }
    try { await api.post("/routes", form); toast.success("Route added"); setOpen(false); setForm({ path_prefix: "/", kind: "redirect", target: "" }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const runResolve = async () => {
    setResult(null);
    try { const r = await api.get("/routing/resolve", { params: test }); setResult(r); }
    catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Resolve failed";
      setResult({ error: msg });
      toast.error(msg);
    }
  };

  return (
    <div data-testid="routing-page">
      <DomainSummary />
      <PageHeader
        title="Routing Engine"
        breadcrumb="cannon / routing"
        subtitle="Server hostname routes first; Cannon sites use Routing rules → Named Link → Slug → Wildcard → Default"
        actions={canWrite && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><button data-testid="new-route-btn" className="inline-flex items-center gap-2 rounded-md bg-zinc-100 text-zinc-900 px-3 py-1.5 text-sm font-medium hover:bg-white"><Plus className="h-4 w-4" /> New Rule</button></DialogTrigger>
            <DialogContent className="bg-[#0c0c0e] border-zinc-800">
              <DialogHeader><DialogTitle className="font-head">Create Routing Rule</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div><label className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Path Prefix</label>
                  <Input value={form.path_prefix} onChange={(e) => setForm({ ...form, path_prefix: e.target.value })} placeholder="/api" className="mt-1.5 bg-zinc-900 border-zinc-800 font-mono" data-testid="route-prefix" /></div>
                <div><label className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Kind</label>
                  <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                    <SelectTrigger className="mt-1.5 bg-zinc-900 border-zinc-800" data-testid="route-kind"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0c0c0e] border-zinc-800"><SelectItem value="redirect">Redirect</SelectItem><SelectItem value="proxy">Local reverse-proxy</SelectItem></SelectContent>
                  </Select></div>
                <div><label className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Target (URL or slug)</label>
                  <Input value={form.target} onChange={(e) => setForm({ ...form, target: e.target.value })} placeholder="https://… or site-slug" className="mt-1.5 bg-zinc-900 border-zinc-800 font-mono" data-testid="route-target" /></div>
              </div>
              <DialogFooter><button onClick={create} data-testid="route-create-btn" className="rounded-md bg-zinc-100 text-zinc-900 px-4 py-2 text-sm font-medium hover:bg-white">Create</button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      />
      <Link to="/freedns" className="inline-block mb-4 text-xs text-sky-400">Manage verified domains, wildcard certificates and exact hostname overrides →</Link>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          {!routes ? <Loading /> : routes.length === 0 ? <EmptyState icon={RouteIcon} title="No routing rules" /> : (
            <div className="border border-zinc-800 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-zinc-800 bg-zinc-900/50 text-left text-xs font-semibold uppercase tracking-widest text-zinc-500">
                  <th className="px-4 py-2.5">Prefix</th><th className="px-4 py-2.5">Kind</th><th className="px-4 py-2.5">Target</th><th className="px-4 py-2.5 w-10"></th></tr></thead>
                <tbody>
                  {routes.map((r) => (
                    <tr key={r.route_id} className="border-b border-zinc-800/70 hover:bg-zinc-900/40" data-testid={`route-row-${r.route_id}`}>
                      <td className="px-4 py-3 font-mono text-xs text-sky-400">{r.path_prefix}</td>
                      <td className="px-4 py-3"><span className={cn("font-mono text-xs uppercase", r.kind === "proxy" ? "text-violet-400" : "text-amber-400")}>{r.kind}</span></td>
                      <td className="px-4 py-3 font-mono text-xs text-zinc-400 truncate max-w-[200px]">{r.target}</td>
                      <td className="px-4 py-3 text-right">{canWrite && <button onClick={async () => { await api.del(`/routes/${r.route_id}`); load(); }} className="p-1 text-zinc-500 hover:text-rose-400"><Trash2 className="h-4 w-4" /></button>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="border border-zinc-800 rounded-lg bg-[#0c0c0e] p-4 h-fit">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-zinc-500 mb-3"><Play className="h-3.5 w-3.5" /> Resolver Preview</div>
          <div className="space-y-2">
            <Input value={test.host} onChange={(e) => setTest({ ...test, host: e.target.value })} placeholder="host" className="bg-zinc-900 border-zinc-800 font-mono text-xs" data-testid="resolve-host" />
            <Input value={test.path} onChange={(e) => setTest({ ...test, path: e.target.value })} placeholder="/path" className="bg-zinc-900 border-zinc-800 font-mono text-xs" data-testid="resolve-path" />
            <button onClick={runResolve} data-testid="resolve-btn" className="w-full rounded-md bg-zinc-100 text-zinc-900 py-2 text-sm font-medium hover:bg-white">Resolve</button>
          </div>
          {result && (
            <div className="mt-4 space-y-2" data-testid="resolve-result">
              {result.error ? (
                <div className="rounded-md border border-rose-500/20 bg-rose-500/5 p-3 font-mono text-xs text-rose-300" data-testid="resolve-error">
                  {result.error}
                </div>
              ) : (
                <>
                  <div className="rounded-md border border-zinc-800 p-3 font-mono text-xs space-y-1">
                    <div className="flex items-center gap-2">
                      <span className={result.matched ? "text-emerald-400" : "text-rose-400"}>{result.matched ? "✓ matched" : "✕ no match"}</span>
                      <span className="text-zinc-600">·</span>
                      <span className="text-sky-400">{result.matchType}</span>
                    </div>
                    {result.siteSlug && <div className="text-zinc-300 flex items-center gap-1">{result.host} <ArrowRight className="h-3 w-3 text-zinc-600" /> <span className="text-violet-400">{result.siteSlug}</span></div>}
                    {result.siteType && <div className="text-zinc-500">type: <span className="text-zinc-300">{result.siteType}</span>{result.siteType === "node" && <> · app: <span className={result.appStatus === "running" ? "text-emerald-400" : "text-amber-400"}>{result.appStatus}</span> · port: <span className="text-zinc-300">{result.internalPort || "—"}</span></>}</div>}
                    {result.target && !result.siteSlug && <div className="text-zinc-300">→ {result.target}</div>}
                    {!result.siteSlug && !result.target && result.matchType === "none" && <div className="text-rose-400">→ 404</div>}
                  </div>
                  <div className="space-y-1">
                    {result.steps?.map((s, i) => (
                      <div key={i} className="flex gap-1.5 font-mono text-[11px] text-zinc-500"><ListOrdered className="h-3 w-3 mt-0.5 shrink-0" /> {s}</div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
